import type { Editor, JSONContent } from '@tiptap/react'
import { useEffect } from 'react'
import { shouldReconcileEditorContent } from '#src/components/rich-text-editor/getCleanedContent.ts'
import { useCurrentRef } from '#src/hooks/useCurrentRef.ts'

export function useSyncEditorContent(
  editor: Editor | null,
  content: string | JSONContent,
  onBeforeContentReplace?: () => void
): void {
  const editorRef = useCurrentRef(editor)
  const onBeforeContentReplaceRef = useCurrentRef(onBeforeContentReplace)
  useEffect(() => {
    // setContent within useEffect needs setTimeout to avoid triggering a warning: https://github.com/ueberdosis/tiptap/issues/3764#issuecomment-1546854730
    const timeout = setTimeout(() => {
      const editor = editorRef.current
      if (!editor || editor.isFocused) {
        return
      }
      // Canonicalize both sides through the editor's schema rather than comparing
      // raw getHTML()/getJSON() to the incoming value: an empty doc always holds
      // a paragraph ("<p></p>"), which never equals an empty string or
      // {type:'doc',content:[]}, so naive compares re-fire setContent every
      // render and grow the heap until OOM. Applies to BOTH branches — JSON
      // content arrives as a fresh object per render from parseNoteContent, so a
      // non-converging structural compare loops just like the string case did.
      if (shouldReconcileEditorContent(editor, content)) {
        onBeforeContentReplaceRef.current?.()
        // ##PreserveWhitespace Keeps spaces between adjacent inline nodes from collapsing; must match the editor's own parseOptions.
        editor.commands.setContent(content, {
          emitUpdate: false,
          parseOptions: { preserveWhitespace: true },
        })
      }
    })
    return () => clearTimeout(timeout)
  }, [content, editorRef, onBeforeContentReplaceRef])
}
